import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FolderClosed, Plus } from "lucide-react";
import { ProjectFormDialog } from "@/components/projects/ProjectFormDialog";
import { useProjects } from "@/hooks/queries";
import { formatRelative } from "@/lib/format";
import { cn } from "@/lib/utils";

function ProjectCard({ p }) {
  return (
    <Link to={`/projects/${p.id}`} className="group flex flex-col rounded-2xl border border-white/[0.08] bg-ink-900 p-5 transition-colors hover:border-white/[0.16] hover:bg-ink-850" data-testid={`project-card-${p.id}`}>
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/[0.08] bg-ink-850">
          <FolderClosed className="h-4 w-4 text-zinc-300" strokeWidth={1.6} />
        </span>
        <span className="min-w-0 flex-1 truncate text-[15px] font-medium text-white" data-testid="project-card-name">{p.name}</span>
      </div>
      <p className="mt-3 line-clamp-2 min-h-[2.5rem] text-[13px] leading-5 text-zinc-500">{p.description || "No description"}</p>
      <div className="mt-4 flex items-center gap-3 text-[12px] text-zinc-500">
        <span>{p.chat_count} {p.chat_count === 1 ? "chat" : "chats"}</span>
        <span>·</span>
        <span>{p.file_count} {p.file_count === 1 ? "file" : "files"}</span>
        <span className="ml-auto">{formatRelative(p.last_activity_at)}</span>
      </div>
    </Link>
  );
}

export default function ProjectsPage() {
  const [status, setStatus] = useState("active");
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useProjects(status);
  const items = data?.items || [];
  return (
    <div className="h-full overflow-y-auto" data-testid="projects-page">
      <div className="mx-auto w-full max-w-[960px] px-4 pb-16 pt-8 sm:px-6 sm:pt-12">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h1 className="text-[1.7rem] font-semibold tracking-tight text-white sm:text-3xl">Projects</h1>
            <p className="mt-1 text-sm text-zinc-500">Group chats and files around a goal. ChatSleek uses the project's context in every chat inside it.</p>
          </div>
          <button type="button" onClick={() => setCreating(true)} className="inline-flex h-10 shrink-0 items-center gap-2 rounded-full bg-white px-4 text-sm font-medium text-ink-950 hover:bg-white/90" data-testid="projects-new-button">
            <Plus className="h-4 w-4" /> New project
          </button>
        </div>
        <div className="mt-8 flex gap-1.5" data-testid="projects-status-tabs">
          {["active", "archived"].map((s) => (
            <button key={s} type="button" onClick={() => setStatus(s)} data-testid={`projects-tab-${s}`} className={cn("rounded-full border px-3 py-1 text-xs capitalize transition-colors", status === s ? "border-white bg-white text-ink-950" : "border-white/10 text-zinc-400 hover:text-white")}>
              {s}
            </button>
          ))}
        </div>
        {isError && <div className="mt-6 rounded-xl border border-white/10 p-4 text-sm text-zinc-300">{error.message} <button type="button" className="underline" onClick={() => refetch()}>Try again</button></div>}
        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="projects-grid">
          {isLoading && [0, 1, 2].map((i) => <div key={i} className="h-[150px] animate-pulse rounded-2xl bg-white/[0.03]" />)}
          {items.map((p) => <ProjectCard key={p.id} p={p} />)}
        </div>
        {!isLoading && !isError && items.length === 0 && (
          <div className="flex flex-col items-center py-20 text-center" data-testid="projects-empty-state">
            <FolderClosed className="h-8 w-8 text-zinc-700" strokeWidth={1.4} />
            <p className="mt-4 text-sm text-zinc-300">{status === "active" ? "No projects yet." : "No archived projects."}</p>
            {status === "active" && <p className="mt-1 text-xs text-zinc-500">Create one to keep related chats and files together.</p>}
          </div>
        )}
      </div>
      {creating && <ProjectFormDialog open onOpenChange={setCreating} onSaved={(p) => navigate(`/projects/${p.id}`)} />}
    </div>
  );
}
